import { useMemo, useState } from "react";
import { useLang } from "@/lib/i18n";
import HOUSES from "@/data/houses.json";

export const SIGN_POS = [[241, 216], [123, 98], [98, 123], [216, 241], [98, 359], [123, 384], [241, 266], [351, 384], [376, 359], [258, 241], [384, 123], [359, 98]];
export const CENTERS = [[241, 120], [126, 46], [46, 126], [120, 241], [46, 356], [126, 436], [241, 362], [356, 436], [436, 356], [362, 241], [436, 126], [356, 46]];
export const LINES = [[10, 10, 472, 472], [10, 472, 472, 10], [241, 10, 472, 241], [472, 241, 241, 472], [241, 472, 10, 241], [10, 241, 241, 10]];
const ABBR = { As: "ascendant", Su: "sun", Mo: "moon", Ma: "mars", Me: "mercury", Ju: "jupiter", Ve: "venus", Sa: "saturn", Ra: "rahu", Ke: "ketu", Ur: "uranus", Ne: "neptune", Pl: "pluto" };
const OUTER = ["uranus", "neptune", "pluto"];
const LEFT = new Set([2, 4]);
const RIGHT = new Set([8, 10]);
const FONT = "Arial, Helvetica, 'Noto Sans Devanagari', Mukta, sans-serif";
const P = { T: [241, 10], R: [472, 241], B: [241, 472], L: [10, 241], TL: [10, 10], TR: [472, 10], BR: [472, 472], BL: [10, 472], C: [241, 241], a: [125.5, 125.5], b: [356.5, 125.5], c: [356.5, 356.5], d: [125.5, 356.5] };
const POLYS = ["T b C a", "TL T a", "TL a L", "L a C d", "L d BL", "BL d B", "B d C c", "B c BR", "BR c R", "R c C b", "R b TR", "TR b T"]
  .map((s) => s.split(" ").map((k) => P[k].join(",")).join(" "));
const SIGN_EN = ["Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo", "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces"];

const HouseInfo = ({ idx, house, testid }) => {
  const { t, mr, planet, sign } = useLang();
  if (idx === null) { return <p className="text-sm text-slate-400 mt-4 text-center" data-testid={`${testid}-hint`}>{t("chart.tapHint")}</p>; }
  const [name, text] = HOUSES[idx][mr ? "mr" : "en"];
  const s = SIGN_EN[Number(house.sign) - 1];
  const planets = house.planets.filter((k) => k !== "ascendant").map(planet).join(", ");
  return (
    <div className="mt-4 rounded-xl border border-[#D4AF37]/30 bg-[#0B1222]/80 p-4 fade-up" data-testid={`${testid}-house-info`}>
      <div className="font-display text-[#F3DC8B]" data-testid={`${testid}-house-info-title`}>{name}</div>
      <p className="text-sm text-slate-300 mt-1 leading-relaxed">{text}</p>
      <p className="text-xs text-slate-400 mt-3">{t("chart.sign")}: <span className="text-slate-200">{s ? sign(s) : house.sign}</span> · {t("chart.planetsIn")}: <span className="text-slate-200">{planets || t("chart.none")}</span></p>
    </div>
  );
};

const nearest = (pts, x, y) => pts.reduce((best, [px, py], i) => {
  const d = (px - x) ** 2 + (py - y) ** 2;
  return d < best.d ? { i, d } : best;
}, { i: 0, d: Infinity }).i;

const parseProkerala = (svg, extras) => {
  const doc = new DOMParser().parseFromString(svg, "image/svg+xml");
  const houses = Array.from({ length: 12 }, () => ({ sign: "", planets: [] }));
  doc.querySelectorAll("text").forEach((el) => {
    const x = parseFloat(el.getAttribute("x"));
    const y = parseFloat(el.getAttribute("y"));
    const cls = el.getAttribute("class") || "";
    if (cls.startsWith("pk-planet-")) houses[nearest(CENTERS, x + 10, y - 5)].planets.push(cls.replace("pk-planet-", ""));
    else houses[nearest(SIGN_POS, x, y)].sign = el.textContent.trim();
  });
  extras.forEach(({ key, sign }) => {
    const h = houses.find((x) => x.sign === String(sign));
    if (h && !h.planets.includes(key)) h.planets.push(key);
  });
  return houses;
};

const fromData = (data) => data.map((h, i) => ({
  sign: String(h.sign_number),
  planets: [...(i === 0 ? ["ascendant"] : []), ...h.planets.map((p) => ABBR[p.abbr])],
}));

const PlanetStack = ({ idx, keys, retro, degrees }) => {
  const { planet } = useLang();
  const sorted = [...keys].sort((a, b) => (a === "ascendant" ? -1 : b === "ascendant" ? 1 : 0));
  const n = sorted.length;
  const side = LEFT.has(idx) || RIGHT.has(idx);
  const fs = (n <= 3 ? 15 : n === 4 ? 14 : n === 5 ? 13 : n === 6 ? 12 : 11) - (side ? (degrees ? 3 : 1.5) : 0);
  const lh = fs + 3;
  const [cx, cy] = CENTERS[idx];
  const x = LEFT.has(idx) ? 13 : RIGHT.has(idx) ? 469 : cx;
  const anchor = LEFT.has(idx) ? "start" : RIGHT.has(idx) ? "end" : "middle";
  const half = ((n - 1) * lh) / 2;
  const startY = Math.min(Math.max(cy - half, 10 + fs), 472 - fs - 2 * half);
  return sorted.map((k, r) => (
    <text key={k} x={x} y={startY + r * lh} textAnchor={anchor} dominantBaseline="middle" fontSize={fs}
      fontFamily={FONT} fontWeight={k === "ascendant" ? 700 : 600}
      fill={k === "ascendant" ? "#F87171" : OUTER.includes(k) ? "#7DD3FC" : "#FDE68A"} data-testid={`planet-label-${k}`}>
      {planet(k)}
      {degrees?.[k] && <tspan fontSize={fs * 0.78} fontWeight="400" fill="#E2E8F0">{` ${degrees[k]}`}</tspan>}
      {retro.has(k) ? " ℞" : ""}
    </text>
  ));
};

export const KundaliChart = ({ svg, data, retro, extras, degrees, title, testid, interactive = false }) => {
  const { t } = useLang();
  const [sel, setSel] = useState(null);
  const houses = useMemo(() => (svg ? parseProkerala(svg, extras) : fromData(data)), [svg, data, extras]);
  return (
    <div className="card-sacred p-5 sm:p-6" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-4 gap-2">
        <h3 className="text-lg sm:text-xl text-[#F3DC8B]">{title}</h3>
        <span className="text-xs text-slate-500">{t("chart.retro")}</span>
      </div>
      <svg viewBox="0 0 482 482" className="w-full max-w-[480px] mx-auto block" role="img" aria-label={title}>
        <defs>
          <linearGradient id={`bg-${testid}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#1a0a10" />
            <stop offset="100%" stopColor="#0B1222" />
          </linearGradient>
        </defs>
        <rect x="10" y="10" width="462" height="462" fill={`url(#bg-${testid})`} stroke="#D4AF37" strokeWidth="2" />
        <g stroke="#D4AF37" strokeOpacity="0.75" strokeWidth="1.4">
          {LINES.map(([x1, y1, x2, y2], i) => <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />)}
        </g>
        {interactive && POLYS.map((pts, i) => (
          <polygon key={`p${i}`} points={pts} onClick={() => setSel(i)} data-testid={`${testid}-house-tap-${i + 1}`}
            role="button" tabIndex={0} aria-label={`House ${i + 1}`} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { setSel(i); } }}
            className="cursor-pointer outline-none [&:hover]:[fill-opacity:0.1] focus-visible:[fill-opacity:0.14]" fill="#D4AF37" fillOpacity={sel === i ? 0.18 : 0} />
        ))}
        <g pointerEvents="none">
        {houses.map((h, i) => (
          <g key={i} data-testid={`${testid}-house-${i + 1}`}>
            <text x={SIGN_POS[i][0]} y={SIGN_POS[i][1]} fontSize="15" fill="#EF4444" fontWeight="700" fontFamily={FONT}>{h.sign}</text>
            <PlanetStack idx={i} keys={h.planets} retro={retro} degrees={degrees} />
          </g>
        ))}
        </g>
      </svg>
      {interactive && <HouseInfo idx={sel} house={sel !== null ? houses[sel] : null} testid={testid} />}
      {svg && <p className="text-[11px] text-slate-500 text-right mt-2" data-testid={`${testid}-source`}>Chart: Prokerala</p>}
    </div>
  );
};
